// src/components/MapaTransportadora.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { MapContainer, TileLayer, GeoJSON, Marker } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import styles from "../style/Transportadora.module.css";

import L from "leaflet";
import markerIcon2x from "leaflet/dist/images/marker-icon-2x.png";
import markerIcon from "leaflet/dist/images/marker-icon.png";
import markerShadow from "leaflet/dist/images/marker-shadow.png";
L.Icon.Default.mergeOptions({ iconRetinaUrl: markerIcon2x, iconUrl: markerIcon, shadowUrl: markerShadow });

const GEOJSON_URL = "https://marketbusters.pro/Dropdrop/map.geojson";
const COMUNAS_URL = "https://marketbusters.pro/Dropdrop/pedidos_por_comuna.php";

const normKey = (s) =>
  (s || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/\s+/g, " ")
    .trim();

const getColor = (n) => {
  if (n >= 8) return "#d73027";
  if (n >= 5) return "#fc8d59";
  if (n >= 2) return "#fee08b";
  if (n >= 1) return "#d9ef8b";
  return "#000000";
};

const labelIcon = (text) =>
  L.divIcon({
    className: styles.labelIcon,
    html: `<div>${text}</div>`,
    iconSize: null,
  });

export default function MapaTransportadora({ refreshKey }) {
  const [geojsonData, setGeojsonData] = useState(null);
  const [conteoComunas, setConteoComunas] = useState({});
  const [map, setMap] = useState(null);
  const geojsonRef = useRef(null);

  // Cargar GeoJSON (al montar y cuando cambie refreshKey)
  useEffect(() => {
    let abort = false;
    (async () => {
      try {
        const res = await fetch(GEOJSON_URL, { cache: "no-store" });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const raw = await res.text();
        const gj = JSON.parse(raw);
        if (!abort) setGeojsonData(gj);
      } catch (e) {
        console.error("Error cargando GeoJSON:", e);
      }
    })();
    return () => void (abort = true);
  }, [refreshKey]);

  // Cargar conteo por comuna (al montar y cuando cambie refreshKey)
  useEffect(() => {
    let abort = false;
    (async () => {
      try {
        const res = await fetch(COMUNAS_URL, { cache: "no-store" });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        if (!abort) setConteoComunas(data || {});
      } catch (e) {
        console.error("Error cargando conteo comunas:", e);
      }
    })();
    return () => void (abort = true);
  }, [refreshKey]);

  const conteoIndex = useMemo(() => {
    const idx = {};
    for (const [name, count] of Object.entries(conteoComunas || {})) {
      idx[normKey(name)] = count;
    }
    return idx;
  }, [conteoComunas]);

  // Fit bounds
  useEffect(() => {
    if (map && geojsonRef.current) {
      const b = geojsonRef.current.getBounds?.();
      if (b && b.isValid()) map.fitBounds(b, { padding: [20, 20] });
    }
  }, [map, geojsonData]);

  // Labels para TODAS las comunas (muestran (N) si hay pedidos)
  const labels = useMemo(() => {
    if (!geojsonData) return [];
    const feats =
      geojsonData.type === "FeatureCollection"
        ? geojsonData.features || []
        : geojsonData.type === "Feature"
        ? [geojsonData]
        : [];

    return feats
      .map((f, i) => {
        try {
          const display =
            f?.properties?.Comuna ||
            f?.properties?.COMUNA ||
            f?.properties?.NOM_COM ||
            f?.properties?.name ||
            `Zona ${i + 1}`;
          const key = normKey(display);
          const count = conteoIndex[key] || 0;

          const tempLayer = L.geoJSON(f);
          const center = tempLayer.getBounds().getCenter();
          tempLayer.remove();

          const text = count > 0 ? `${display} (${count})` : display;
          return { id: i, text, pos: [center.lat, center.lng] };
        } catch {
          return null;
        }
      })
      .filter(Boolean);
  }, [geojsonData, conteoIndex]);

  return (
    <div className={`${styles.card} ${styles.mapCard}`}>
      <div className={styles.cardHeader}>
        <h3 className={styles.cardTitle}>Mapa — Pedidos por Comuna</h3>
      </div>

      <div className={styles.mapCanvas}>
        <MapContainer
          center={[-33.45, -70.66]}
          zoom={11}
          scrollWheelZoom
          whenCreated={setMap}
          style={{ width: "100%", height: "100%" }}
        >
          <TileLayer
            url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
            attribution='&copy; OpenStreetMap contributors &copy; CARTO'
          />

          {geojsonData && (
            <>
              <GeoJSON
                data={geojsonData}
                ref={geojsonRef}
                style={(feature) => {
                  const nombre =
                    feature?.properties?.Comuna ||
                    feature?.properties?.COMUNA ||
                    feature?.properties?.NOM_COM ||
                    feature?.properties?.name ||
                    "";
                  const key = normKey(nombre);
                  const count = conteoIndex[key] || 0;

                  return {
                    color: "#9aa3b2",
                    opacity: 0.6,
                    weight: 1,
                    fillColor: getColor(count),
                    fillOpacity: count > 0 ? 0.75 : 0, // transparente si no hay pedidos
                  };
                }}
                onEachFeature={(feature, layer) => {
                  const nombre =
                    feature?.properties?.Comuna ||
                    feature?.properties?.COMUNA ||
                    feature?.properties?.NOM_COM ||
                    feature?.properties?.name ||
                    "Zona";
                  const key = normKey(nombre);
                  const count = conteoIndex[key] || 0;
                  layer.bindPopup(count > 0 ? `${nombre} (${count}) pedidos` : `${nombre} (0)`);
                }}
              />

              {labels.map((lbl) => (
                <Marker key={lbl.id} position={lbl.pos} icon={labelIcon(lbl.text)} interactive={false} />
              ))}
            </>
          )}
        </MapContainer>
      </div>
    </div>
  );
}
